import { Link } from 'react-router-dom';
import React, { useEffect } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { Header } from './Header';
import { Footer } from './Footer';
import { RelatedLocations } from './RelatedLocations';
import { LocationFAQ } from './LocationFAQ';
import { SEO } from './SEO';
import { 
  Server, Zap, Shield, Globe, 
  CheckCircle, HardDrive, Network,
  Clock, MapPin, TrendingUp, Database,
  Building2, Radio, Activity, Eye
} from 'lucide-react';
import { Button } from './ui/button';
import { PricingSection } from './PricingSection';
import { PaymentMethodsSection } from './PaymentMethodsSection';
import { smoothScrollToWithOffset } from '../lib/scroll';

export const ItalyPage = () => {
  const { t, language } = useLanguage();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const stats = [
    { icon: Clock, value: t('italy.stats.latency'), label: t('italy.stats.latencyLabel') },
    { icon: Zap, value: t('italy.stats.speed'), label: t('italy.stats.speedLabel'), wiki: 'shared-1gbps-port-explained' },
    { icon: TrendingUp, value: t('italy.stats.uptime'), label: t('italy.stats.uptimeLabel') },
    { icon: MapPin, value: t('italy.stats.location'), label: t('italy.stats.locationLabel') }
  ];

  const whyItaly = [
    {
      icon: Globe,
      title: t('italy.why.gateway.title'),
      description: t('italy.why.gateway.desc')
    },
    {
      icon: Server,
      title: t('italy.why.infrastructure.title'),
      description: t('italy.why.infrastructure.desc')
    },
    {
      icon: Shield,
      title: t('italy.why.compliance.title'),
      description: t('italy.why.compliance.desc')
    },
    {
      icon: Network,
      title: t('italy.why.connectivity.title'),
      description: t('italy.why.connectivity.desc')
    }
  ];

  const features = [
    {
      icon: HardDrive,
      title: t('italy.features.nvme.title'),
      description: t('italy.features.nvme.desc')
    },
    {
      icon: Shield,
      title: t('italy.features.security.title'),
      description: t('italy.features.security.desc')
    },
    {
      icon: Clock,
      title: t('italy.features.deployment.title'),
      description: t('italy.features.deployment.desc')
    },
    {
      icon: Database,
      title: t('italy.features.backup.title'),
      description: t('italy.features.backup.desc')
    },
    {
      icon: Network,
      title: t('italy.features.network.title'),
      description: t('italy.features.network.desc')
    },
    {
      icon: Eye,
      title: t('italy.features.monitoring.title'),
      description: t('italy.features.monitoring.desc')
    }
  ];

  const scrollToPricing = () => smoothScrollToWithOffset('pricing', 100);

  return (
    <div className="min-h-screen bg-white">
      <SEO page="italy" />
      <Header />
      
      {/* Hero Section - Italian Green theme */}
      <section className="relative py-20 bg-gradient-to-br from-green-800 via-green-700 to-green-800 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-10 w-72 h-72 bg-green-600 rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 right-20 w-96 h-96 bg-green-700 rounded-full blur-3xl"></div>
        </div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-green-700/20 border border-green-600/30 rounded-full text-green-50 text-sm font-semibold mb-6">
              <MapPin className="w-4 h-4" />
              <span>{t('italy.hero.badge')}</span>
            </div>
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
              {t('italy.hero.title')}
            </h1>
            <p className="text-xl text-green-50 max-w-3xl mx-auto mb-8">
              {t('italy.hero.subtitle')}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                onClick={scrollToPricing}
                className="bg-white text-green-800 hover:bg-green-50 font-semibold px-8 py-6 text-lg"
              >
                {t('italy.hero.getStarted')}
              </Button>
              <Button 
                onClick={scrollToPricing}
                className="bg-green-950 text-white hover:bg-black font-semibold px-8 py-6 text-lg"
              >
                {t('italy.hero.viewPlans')}
              </Button>
            </div>
          </div>
          
          {/* Milan Images Gallery */}
          <div className="grid md:grid-cols-2 gap-6 max-w-6xl mx-auto">
            <div className="rounded-2xl overflow-hidden shadow-2xl">
              <img src="/images/locations/unsplash_1610016302534-6f67f1c968d8.jpg" 
                alt="Milan Duomo Cathedral at Sunset" loading="lazy"
                className="w-full h-80 object-cover"
 loading="lazy"/>
            </div>
            <div className="rounded-2xl overflow-hidden shadow-2xl">
              <img src="/images/locations/unsplash_1683387656362-1cd5cf2ddf70.jpg" 
                alt="Milan Modern Skyline - Bosco Verticale" loading="lazy"
                className="w-full h-80 object-cover"
 loading="lazy"/>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => {
              const IconComponent = stat.icon;
              return (
                <div key={index} className="text-center">
                  <div className="inline-flex items-center justify-center w-12 h-12 bg-green-100 text-green-600 rounded-lg mb-3">
                    <IconComponent className="w-6 h-6" />
                  </div>
                  <div className="text-3xl font-bold text-gray-900 mb-1">{stat.value}</div>
                  <div className="text-sm text-gray-600">{stat.label}</div>
                  {stat.wiki && (
                    <Link
                      to={(language === 'ru' ? '/ru' : '') + '/wiki/' + stat.wiki}
                      className="mt-2 inline-flex items-center gap-1 text-xs text-blue-300 hover:text-white transition-colors"
                    >
                      {language === 'ru' ? 'Что значит общий 1 Гбит/с?' : 'What does shared 1 Gbps mean?'} →
                    </Link>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Why Italy Section */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-green-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              {t('italy.why.title')}
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              {t('italy.why.subtitle')}
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {whyItaly.map((item, index) => {
              const IconComponent = item.icon;
              return (
                <div key={index} className="bg-white p-8 rounded-xl shadow-lg hover:shadow-xl transition-shadow">
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 w-12 h-12 bg-green-100 text-green-600 rounded-lg flex items-center justify-center">
                      <IconComponent className="w-6 h-6" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-gray-900 mb-2">{item.title}</h3>
                      <p className="text-gray-600">{item.description}</p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              {t('italy.features.title')}
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              {t('italy.features.subtitle')}
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => {
              const IconComponent = feature.icon;
              return (
                <div key={index} className="bg-gradient-to-br from-gray-50 to-green-50 p-6 rounded-xl hover:shadow-lg transition-shadow">
                  <div className="w-12 h-12 bg-green-600 text-white rounded-lg flex items-center justify-center mb-4">
                    <IconComponent className="w-6 h-6" />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">{feature.title}</h3>
                  <p className="text-gray-600 text-sm">{feature.description}</p>
                </div>
              );
            })}          </div>
        </div>
      </section>      {/* Related Locations - Internal Links for SEO */}
      <RelatedLocations currentLocation="vps-italy" />



      {/* Pricing Section */}
      <div id="pricing">
        <PricingSection 
          hideHeader={false}
          title={t('italy.pricing.title')}
          subtitle={t('italy.pricing.subtitle')}
        />
      </div>

      {/* Payment Methods */}
      <PaymentMethodsSection transparentBg={false} />

      {/* Datacenter Infrastructure Section */}
      <section className="py-20 bg-gradient-to-br from-green-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold text-gray-900 mb-6">
                {t('italy.infrastructure.title')}
              </h2>
              <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                {t('italy.infrastructure.description')}
              </p>
              <div className="space-y-4">
                {[
                  { icon: Building2, text: t('italy.infrastructure.item1') },
                  { icon: Radio, text: t('italy.infrastructure.item2') },
                  { icon: Activity, text: t('italy.infrastructure.item3') },
                  { icon: Shield, text: t('italy.infrastructure.item4') }
                ].map((item, index) => {
                  const IconComponent = item.icon;
                  return (
                    <div key={index} className="flex items-start gap-3">
                      <div className="flex-shrink-0 w-10 h-10 bg-green-100 text-green-600 rounded-lg flex items-center justify-center">
                        <IconComponent className="w-5 h-5" />
                      </div>
                      <p className="text-gray-700 pt-2">{item.text}</p>
                    </div>
                  );
                })}
              </div>
            </div>
            <div className="space-y-6">
              <div className="rounded-2xl overflow-hidden shadow-xl">
                <img src="/images/locations/unsplash_1626365266361-b1e53f45c876.jpg" 
                  alt="Milan Duomo Cathedral Architecture" loading="lazy"
                  className="w-full h-72 object-cover"
 loading="lazy"/>
              </div>
              <div className="grid grid-cols-2 gap-6">
                <div className="rounded-xl overflow-hidden shadow-lg">
                  <img src="/images/locations/unsplash_1578731397127-52e569dcb46d.jpg" 
                    alt="Milan Aerial Skyline" loading="lazy"
                    className="w-full h-48 object-cover"
 loading="lazy"/>
                </div>
                <div className="rounded-xl overflow-hidden shadow-lg">
                  <img src="/images/locations/unsplash_1742148186848-8b257455009b.jpg" 
                    alt="Milan Modern City" loading="lazy"
                    className="w-full h-48 object-cover"
 loading="lazy"/>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Location Details */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <div className="rounded-2xl overflow-hidden shadow-xl">
                <img src="/images/locations/unsplash_1542277235-f9877df42ad7.jpg" 
                  alt="Milan Duomo Cathedral Details" loading="lazy"
                  className="w-full h-80 object-cover"
 loading="lazy"/>
              </div>
              <div className="rounded-lg overflow-hidden shadow-md">
                <img src="/images/locations/320px-Flag_of_Italy.svg.png" 
                  alt="Italy Flag" loading="lazy"
                  className="w-full h-32 object-cover"
 loading="lazy"/>
              </div>
            </div>
            <div>
              <h2 className="text-4xl font-bold text-gray-900 mb-6">
                {t('italy.location.title')}
              </h2>
              <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                {t('italy.location.description')}
              </p>
              <div className="bg-green-50 border-l-4 border-green-600 p-6 rounded-r-lg mb-6">
                <h3 className="font-bold text-gray-900 mb-2 flex items-center gap-2">
                  <MapPin className="w-5 h-5 text-green-600" />
                  {t('italy.location.subtitle')}
                </h3>
                <p className="text-gray-700">{t('italy.location.details')}</p>
              </div>
              <ul className="space-y-3">
                {t('italy.location.benefits').map((benefit, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">{benefit}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-green-800 via-green-700 to-green-800">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold text-white mb-6">
            {t('italy.cta.title')}
          </h2>
          <p className="text-xl text-green-100 mb-8">
            {t('italy.cta.subtitle')}
          </p>
          <Button 
            onClick={scrollToPricing}
            className="bg-white text-green-900 hover:bg-green-50 font-semibold px-8 py-6 text-lg"
          >
            {t('italy.cta.button')}
          </Button>
        </div>
      </section>

      <LocationFAQ location="vps-italy" />
      <Footer />
    </div>
  );
};

